import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, IconButton, Button,
  CircularProgress, Chip, Dialog, DialogContent,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { colors, shadows, typography } from '../theme/designTheme';
import { useScoredRedFlags } from '../hooks/useScoredRedFlags';

pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const sourceLabel = {
  asic:                'ASIC',
  corelogic:           'CoreLogic',
  abr:                 'ABR/GST',
  credit:              'Credit',
  application:         'Application',
  valuation:           'Valuation',
  transaction:         'Transaction',
  transactions:        'Transactions',
  cases:               'Related Case',
  financial_statement: 'Financial Statement',
  regulation:          'Regulation',
  sanctions_screening: 'Sanctions Screening',
  graph:               'Graph',
  core_banking:        'Core Banking',
  policy:              'Policy',
  lc:                  'Letter of Credit',
  invoice:             'Commercial Invoice',
  bol:                 'Bill of Lading',
  red_flag:            'Red Flag',
};

const audFmt0 = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const aud = (n) => (typeof n === 'number' ? audFmt0.format(n) : '—');
const pct = (p) => (typeof p === 'number' ? `${(p * 100).toFixed(1)}%` : '—');

const CATEGORY_CONFIGS = {
  network: {
    key:      'network',
    label:    'Network Signals',
    subtitle: 'Relationship & cluster-level risk across entities.',
    Icon:     HubOutlinedIcon,
    accent:   colors.critical,
    tint:     colors.errorContainer,
    variant:  'network',
  },
  fraud: {
    key:      'fraud',
    label:    'Fraud Signals',
    subtitle: 'Direct indicators of first-party or third-party fraud.',
    Icon:     ReportProblemOutlinedIcon,
    accent:   colors.primary,
    tint:     colors.primaryContainer,
    variant:  'fraud',
  },
  sanctions: {
    key:      'sanctions',
    label:    'Sanctions Signals',
    subtitle: 'Direct sanctions exposure — lists, jurisdictions, evasion patterns.',
    Icon:     GavelOutlinedIcon,
    accent:   colors.primary,
    tint:     colors.primaryContainer,
    variant:  'sanctions',
  },
  risk_multiplier: {
    key:      'risk_multiplier',
    label:    'Risk Multipliers',
    subtitle: 'Factors that compound the underlying risk.',
    Icon:     AccessTimeOutlinedIcon,
    accent:   colors.purple,
    tint:     colors.purpleBg,
    variant:  'risk',
  },
};

// Three-card roster per case type. Sanctions cases show Network / Sanctions /
// Risk multiplier; application-fraud cases show Network / Fraud / Risk
// multiplier (unchanged).
const CATEGORY_ROSTERS = {
  sanctions:         ['network', 'sanctions', 'risk_multiplier'],
  application_fraud: ['network', 'fraud',     'risk_multiplier'],
};

const rosterFor = (flags) => {
  const hasSanctions = flags.some((f) => f.category === 'sanctions');
  const hasFraud     = flags.some((f) => f.category === 'fraud');
  if (hasSanctions) return CATEGORY_ROSTERS.sanctions;
  if (hasFraud)     return CATEGORY_ROSTERS.application_fraud;
  return CATEGORY_ROSTERS.application_fraud;
};

// ── Mini bar chart (decorative trend) ─────────────────────────────────
const MiniTrend = ({ heights, accent }) => (
  <Box sx={{ display: 'flex', gap: 0.5, height: 22, alignItems: 'flex-end', mb: 1.75 }}>
    {heights.map((h, i) => (
      <Box key={i} sx={{
        flex: 1, height: `${h}%`, bgcolor: accent, opacity: 0.5 + (h / 200),
        borderRadius: '2px 2px 0 0',
      }} />
    ))}
  </Box>
);

// ── Category card ─────────────────────────────────────────────────────

const heightsForCount = (count, accent) => {
  // deterministic growth pattern that scales with count
  const base = Math.min(0.15 + count / 10, 1);
  return [0.3, 0.45, 0.6, 0.72, 0.78, 0.88, 1].map((r) => Math.round(r * base * 100));
};

const CategoryCard = ({ cfg, count, onClick }) => {
  const Icon = cfg.Icon;
  const empty = count === 0;
  const heights = heightsForCount(count || 1, cfg.accent);
  const displayAccent = empty ? colors.success : cfg.accent;

  return (
    <Box
      onClick={empty ? undefined : onClick}
      sx={{
        bgcolor: colors.surface, borderRadius: '16px',
        p: '20px 22px 18px',
        boxShadow: shadows.ambient,
        position: 'relative', overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: empty ? 'default' : 'pointer',
        '&:hover': empty ? {} : { transform: 'translateY(-2px)', boxShadow: shadows.lift },
      }}
    >
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        bgcolor: cfg.accent, opacity: 0.9,
      }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          bgcolor: cfg.tint,
          display: 'grid', placeItems: 'center',
        }}>
          <Icon sx={{ color: cfg.accent, fontSize: 18 }} />
        </Box>
        {empty ? (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            bgcolor: colors.canvas, color: colors.textSecondary,
            px: 1, py: 0.5, borderRadius: '6px',
          }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.6 }} />
            Clean
          </Box>
        ) : (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            bgcolor: cfg.tint, color: cfg.accent,
            px: 1, py: 0.5, borderRadius: '6px',
          }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor', opacity: 0.6 }} />
            {count} {count === 1 ? 'flag' : 'flags'}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
        <Typography sx={{
          fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '40px',
          lineHeight: 1, letterSpacing: '-0.03em',
          color: displayAccent,
        }}>
          {count}
        </Typography>
        <Typography sx={{
          fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase',
          color: colors.textSecondary, fontWeight: 600,
        }}>
          Flags
        </Typography>
      </Box>

      <Typography sx={{
        fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '15px',
        letterSpacing: '-0.005em', mb: 0.5, color: colors.textPrimary,
      }}>
        {cfg.label}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5, mb: 1.75 }}>
        {cfg.subtitle}
      </Typography>

      <MiniTrend heights={heights} accent={cfg.accent} />

      <Box sx={{
        display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
        pt: 1.5, borderTop: `1px solid ${colors.canvas}`,
      }}>
        <Box
          component="a"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            fontSize: '12px', fontWeight: 600, color: cfg.accent,
            textDecoration: 'none', cursor: empty ? 'default' : 'pointer',
          }}
        >
          {empty ? 'No action' : 'View details'}
          <ArrowForwardIcon sx={{ fontSize: 12 }} />
        </Box>
      </Box>
    </Box>
  );
};

// ── Doc viewer + PDF drawer (preserved from original) ─────────────────

const ViewDocumentButton = ({ doc, onView }) => {
  if (!doc?.url || !onView) return null;
  return (
    <Button
      variant="outlined" size="small"
      startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />}
      onClick={() => onView(doc)}
      sx={{
        mt: 1.5, borderColor: colors.border, color: colors.textPrimary, bgcolor: colors.surface,
        fontFamily: typography.fontFamily, fontSize: '11px', fontWeight: 600,
        textTransform: 'none', letterSpacing: '0.02em',
        '&:hover': { bgcolor: colors.surfaceElevated, borderColor: colors.accent, color: colors.accent },
      }}
    >
      View Source Document
    </Button>
  );
};

// ── Split-pane PDF viewer (react-pdf) ─────────────────────────────────

const normalizeText = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

const buildTextRenderer = (anchor) => {
  if (!anchor) return undefined;
  const normAnchor = normalizeText(anchor);
  if (!normAnchor) return undefined;
  const needle = normAnchor.slice(0, Math.min(40, normAnchor.length));
  return ({ str }) => {
    const normStr = normalizeText(str);
    if (!normStr || normStr.length < 3) return str;
    const hit =
      normAnchor.includes(normStr) ||
      normStr.includes(needle) ||
      (normStr.length > 6 && normAnchor.includes(normStr.slice(0, 8)));
    if (!hit) return str;
    const safe = str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<mark class="lisa-pdf-hit">${safe}</mark>`;
  };
};

const PdfSplitPanel = ({ doc, activePage, activeAnchor, onClose }) => {
  const [numPages, setNumPages] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollRef = useRef(null);
  const pageRefs = useRef({});
  const docUrl = doc?.url || null;

  // When the active document changes (multi-doc flags), reset local state
  // *before* react-pdf renders new Page children. Leaving stale numPages or
  // pageRefs around causes Page components to call getPage() on a destroyed
  // WorkerTransport → "Cannot read properties of null (reading 'sendWithPromise')".
  useEffect(() => {
    setNumPages(0);
    pageRefs.current = {};
  }, [docUrl]);

  useEffect(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const measure = () => setContainerWidth(el.clientWidth - 48);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [docUrl]);

  useEffect(() => {
    if (!activePage || !numPages) return;
    const node = pageRefs.current[activePage];
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activePage, activeAnchor, numPages]);

  const textRenderer = useMemo(() => buildTextRenderer(activeAnchor), [activeAnchor]);

  if (!doc) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: colors.bg, minWidth: 0 }}>
      <Box sx={{
        px: 2.5, py: 1.5, borderBottom: `1px solid ${colors.canvas}`, bgcolor: colors.surface,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexShrink: 0,
      }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            color: colors.textPrimary, fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '13px',
            letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {doc.label || 'Source Document'}
          </Typography>
          {Array.isArray(doc.refs) && doc.refs.length > 0 && (
            <Typography sx={{
              color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '10px', fontWeight: 600,
              letterSpacing: '0.06em', mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {doc.refs.join('  ·  ')}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => window.open(doc.url, '_blank', 'noopener,noreferrer')} title="Open in new tab"
            sx={{ color: colors.textSecondary, '&:hover': { color: colors.accent, bgcolor: colors.accentDim } }}>
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={onClose} title="Close source"
            sx={{ color: colors.textSecondary, '&:hover': { color: colors.textPrimary, bgcolor: colors.surfaceElevated } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.5, bgcolor: '#2b2b2b',
        '& .lisa-pdf-hit': {
          background: 'rgba(255, 224, 102, 0.9)',
          mixBlendMode: 'multiply',
          padding: '0 1px',
          borderRadius: '2px',
          animation: 'lisaPdfHitPulse 0.6s ease-out',
        },
        '@keyframes lisaPdfHitPulse': {
          '0%':   { background: 'rgba(255, 145, 0, 0.95)' },
          '100%': { background: 'rgba(255, 224, 102, 0.9)' },
        },
        '& .react-pdf__Page': {
          marginBottom: '18px',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
          borderRadius: '4px',
          overflow: 'hidden',
        },
      }}>
        <Document
          key={docUrl}
          file={docUrl}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => setNumPages(0)}
          onSourceError={() => setNumPages(0)}
          loading={
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={22} sx={{ color: colors.accent }} />
            </Box>
          }
          error={
            <Typography sx={{ color: '#ffb4b4', fontFamily: typography.fontFamily, fontSize: '12px', textAlign: 'center', py: 4 }}>
              Could not load PDF.
            </Typography>
          }
        >
          {Array.from({ length: numPages }, (_, i) => {
            const pageNumber = i + 1;
            const isActive = pageNumber === activePage;
            return (
              <Box
                key={pageNumber}
                ref={(el) => { pageRefs.current[pageNumber] = el; }}
                sx={{
                  outline: isActive ? `3px solid ${colors.accent}` : 'none',
                  outlineOffset: '2px',
                  borderRadius: '4px',
                  transition: 'outline 0.2s',
                }}
              >
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth > 0 ? containerWidth : undefined}
                  customTextRenderer={textRenderer}
                  renderAnnotationLayer={false}
                />
              </Box>
            );
          })}
        </Document>
      </Box>
    </Box>
  );
};

// ── Payroll vs super reconciliation (preserved) ───────────────────────

const PayrollSuperReconciliation = ({ data, onViewDoc, onRowHover, pdfOpen }) => {
  const { inputs = {}, computed = {}, period, source_document } = data;
  const sgRatePct = inputs.sg_rate ? `${(inputs.sg_rate * 100).toFixed(1)}%` : null;
  const rows = source_document?.rows || {};

  const Row = ({ label, value, note, emphasis, deficit, rowKey }) => {
    const hoverable = pdfOpen && rowKey && rows[rowKey];
    return (
      <Box
        onMouseEnter={hoverable ? () => onRowHover(rows[rowKey]) : undefined}
        sx={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', py: 0.6,
          px: 0.75, mx: -0.75, borderRadius: '6px',
          cursor: hoverable ? 'pointer' : 'default',
          transition: 'background 0.15s',
          ...(hoverable && {
            '&:hover': {
              bgcolor: 'rgba(255,224,102,0.18)',
              outline: `1px solid ${colors.accent}`,
            },
          }),
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: emphasis ? 700 : 500 }}>
            {label}
          </Typography>
          {note && (
            <Typography sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '10px', mt: 0.15 }}>
              {note}
            </Typography>
          )}
        </Box>
        <Typography sx={{
          color: deficit ? colors.critical : colors.textPrimary,
          fontFamily: typography.fontFamilyMono,
          fontSize: emphasis ? '14px' : '13px',
          fontWeight: emphasis ? 700 : 500,
          whiteSpace: 'nowrap', ml: 2,
        }}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{
      mb: 2.5, bgcolor: colors.errorContainer,
      borderLeft: `3px solid ${colors.critical}`,
      borderRadius: '10px', p: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Typography sx={{
          color: colors.critical, fontFamily: typography.fontFamily,
          fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em',
        }}>
          AI EXTRACT — PAYROLL vs SUPERANNUATION
        </Typography>
        {period && (
          <Typography sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '10px', fontWeight: 600 }}>
            {period}
          </Typography>
        )}
      </Box>
      <Row rowKey="wages" label="Wages and salaries" note="Note 6 — declared payroll expense" value={aud(inputs.wages)} />
      <Row rowKey="super_actual" label="Superannuation expense" note="Note 7 — actually remitted" value={aud(inputs.super_actual)} />
      <Box sx={{ borderTop: `1px dashed rgba(187,27,33,0.35)`, my: 1 }} />
      <Row rowKey="super_expected" label={`Expected super${sgRatePct ? ` @ ${sgRatePct} SG rate` : ''}`} note="Statutory benchmark" value={aud(computed.super_expected)} />
      <Row rowKey="shortfall" label="Shortfall" value={`${aud(-computed.shortfall)}  (${pct(computed.shortfall_pct)})`} emphasis deficit />
      {(computed.implied_employees_actual != null || computed.implied_employees_declared != null) && (
        <Box sx={{
          mt: 1.5, p: 1.25, borderRadius: '10px',
          bgcolor: colors.surface,
        }}>
          <Typography sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '11px', lineHeight: 1.55 }}>
            Note 7 records contributions for{' '}
            <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              {computed.implied_employees_actual} eligible employees
            </Box>{' '}— Note 6 implies a workforce of{' '}
            <Box component="span" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
              ~{computed.implied_employees_declared} FTE
            </Box>.
          </Typography>
        </Box>
      )}
      <ViewDocumentButton doc={source_document} onView={onViewDoc} />
    </Box>
  );
};

// ── Trade document destination mismatch (multi-doc) ───────────────────

const TradeDocMismatch = ({ data, onViewDoc, onRowHover, pdfOpen }) => {
  const { inputs = {}, computed = {}, source_documents = [] } = data || {};
  const rows = data?.rows || {};
  const docByKey = useMemo(
    () => Object.fromEntries(source_documents.map((d) => [d.key, d])),
    [source_documents],
  );

  const Row = ({ rowKey, label, value, tone = 'neutral' }) => {
    const row = rows[rowKey];
    const hoverable = pdfOpen && !!row;
    const toneColor =
      tone === 'conflict' ? colors.critical :
      tone === 'ok'       ? colors.success  :
                            colors.textPrimary;
    const Marker =
      tone === 'conflict' ? WarningAmberOutlinedIcon :
      tone === 'ok'       ? CheckCircleOutlineIcon   :
                            null;
    return (
      <Box
        onMouseEnter={hoverable ? () => onRowHover({ ...row, doc: row.doc }) : undefined}
        onClick={hoverable ? () => onRowHover({ ...row, doc: row.doc }) : undefined}
        sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5,
          py: 0.75, px: 0.75, mx: -0.75, borderRadius: '6px',
          cursor: hoverable ? 'pointer' : 'default',
          transition: 'background 0.15s',
          ...(hoverable && {
            '&:hover': {
              bgcolor: 'rgba(255,224,102,0.18)',
              outline: `1px solid ${colors.accent}`,
            },
          }),
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            color: colors.textPrimary, fontFamily: typography.fontFamily,
            fontSize: '12px', fontWeight: 600, lineHeight: 1.4,
          }}>
            {label}
          </Typography>
          <Typography sx={{
            color: toneColor, fontFamily: typography.fontFamilyMono,
            fontSize: '12px', fontWeight: tone === 'conflict' ? 700 : 500, mt: 0.25, lineHeight: 1.4,
            wordBreak: 'break-word',
          }}>
            {value || '—'}
          </Typography>
        </Box>
        {Marker && (
          <Marker sx={{ fontSize: 16, color: toneColor, mt: 0.25, flexShrink: 0 }} />
        )}
      </Box>
    );
  };

  const Section = ({ docKey, title, children }) => {
    const doc = docByKey[docKey];
    return (
      <Box sx={{ mb: 1.25 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          mb: 0.5, pb: 0.5, borderBottom: `1px dashed rgba(0,0,0,0.12)`,
        }}>
          <Typography sx={{
            color: colors.textSecondary, fontFamily: typography.fontFamily,
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
          }}>
            {title}
          </Typography>
          {doc && (
            <Button
              variant="text" size="small"
              startIcon={<PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => onViewDoc(doc)}
              sx={{
                color: colors.textSecondary, fontFamily: typography.fontFamily,
                fontSize: '10px', fontWeight: 600, textTransform: 'none',
                minWidth: 0, px: 0.75, py: 0.25,
                '&:hover': { color: colors.accent, bgcolor: 'transparent' },
              }}
            >
              Open
            </Button>
          )}
        </Box>
        {children}
      </Box>
    );
  };

  const shipmentRef   = data?.shipment_reference;
  const declared      = computed?.declared_destination_country;
  const actual        = computed?.actual_destination_country;
  const programs      = Array.isArray(computed?.sanctions_programs) ? computed.sanctions_programs.join(', ') : '';
  const verdict       = (data?.verdict || '').toUpperCase();

  return (
    <Box sx={{
      mb: 2.5, bgcolor: colors.errorContainer,
      borderLeft: `3px solid ${colors.critical}`,
      borderRadius: '10px', p: 2,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, gap: 1 }}>
        <Typography sx={{
          color: colors.critical, fontFamily: typography.fontFamily,
          fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em',
        }}>
          AI EXTRACT — TRADE DOCUMENT DESTINATION CHECK
        </Typography>
        {shipmentRef && (
          <Typography sx={{
            color: colors.textSecondary, fontFamily: typography.fontFamilyMono,
            fontSize: '10px', fontWeight: 600,
          }}>
            {shipmentRef}
          </Typography>
        )}
      </Box>

      {(declared || actual) && (
        <Box sx={{
          mb: 1.5, px: 1.25, py: 0.85, borderRadius: '8px', bgcolor: colors.surface,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: typography.fontFamily, fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: colors.textSecondary }}>
              DECLARED
            </Typography>
            <Typography sx={{ fontFamily: typography.fontFamilyMono, fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>
              {declared || '—'}
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: typography.fontFamily, fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: colors.critical }}>
              ACTUAL
            </Typography>
            <Typography sx={{ fontFamily: typography.fontFamilyMono, fontSize: '12px', fontWeight: 700, color: colors.critical }}>
              {actual || '—'}
            </Typography>
          </Box>
        </Box>
      )}

      <Section docKey="lc" title="LETTER OF CREDIT">
        <Row rowKey="lc_place_of_final_destination"
             label="Place of Final Destination (44B)"
             value={inputs.lc_place_of_final_destination}
             tone="ok" />
      </Section>

      <Section docKey="invoice" title="COMMERCIAL INVOICE">
        <Row rowKey="invoice_ship_to"
             label="Ship To"
             value={inputs.invoice_ship_to}
             tone="ok" />
      </Section>

      <Section docKey="bol" title="BILL OF LADING">
        <Row rowKey="bol_place_of_delivery"
             label="Place of Delivery"
             value={inputs.bol_place_of_delivery}
             tone="conflict" />
        <Row rowKey="bol_consignee"
             label="Consignee"
             value={inputs.bol_consignee}
             tone="conflict" />
        <Row rowKey="bol_notify_party"
             label="Notify Party"
             value={inputs.bol_notify_party}
             tone="conflict" />
        <Row rowKey="bol_transshipment_port"
             label="Transshipment Port"
             value={inputs.bol_transshipment_port}
             tone="neutral" />
      </Section>

      {(verdict || programs) && (
        <Box sx={{
          mt: 1.25, px: 1.25, py: 0.85, borderRadius: '8px', bgcolor: colors.critical,
        }}>
          <Typography sx={{
            color: '#fff', fontFamily: typography.fontFamily,
            fontSize: '11px', fontWeight: 700, lineHeight: 1.4,
          }}>
            {verdict || 'REVIEW'} — routing to sanctioned jurisdiction.{programs ? ` Sanctions programmes: ${programs}.` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const EXTRACT_RENDERERS = {
  payroll_super_check:           PayrollSuperReconciliation,
  trade_doc_destination_check:   TradeDocMismatch,
};
const ExtractedDataCard = ({ extracted, onViewDoc, onRowHover, pdfOpen }) => {
  const Renderer = extracted?.type ? EXTRACT_RENDERERS[extracted.type] : null;
  if (!Renderer) return null;
  return <Renderer data={extracted} onViewDoc={onViewDoc} onRowHover={onRowHover} pdfOpen={pdfOpen} />;
};

// ── Detail modal ──────────────────────────────────────────────────────

// Render description text. Lines prefixed with "• " or "- " become a clean
// bulleted list with hanging indent. Lines without a bullet marker render as
// plain paragraphs — so legacy prose descriptions still look right.
const DescriptionBlock = ({ text }) => {
  if (!text) return null;
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const isBulleted = lines.length > 1 && lines.every((l) => /^([•\-*])\s+/.test(l));
  if (isBulleted) {
    return (
      <Box component="ul" sx={{
        m: 0, mb: 2.5, pl: 2.25,
        color: colors.textSecondary, fontFamily: typography.fontFamilySerif,
        fontSize: '14px', lineHeight: 1.65,
        '& li': { mb: 0.75, pl: 0.25 },
        '& li:last-of-type': { mb: 0 },
        '& li::marker': { color: colors.textSecondary, fontSize: '12px' },
      }}>
        {lines.map((l, i) => (
          <li key={i}>{l.replace(/^[•\-*]\s+/, '')}</li>
        ))}
      </Box>
    );
  }
  return (
    <Typography sx={{
      color: colors.textSecondary, fontFamily: typography.fontFamilySerif,
      fontSize: '14px', lineHeight: 1.65, mb: 2.5, whiteSpace: 'pre-line',
    }}>
      {text}
    </Typography>
  );
};

const moneyFmt = (amount, currency = 'USD') => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(amount));
  } catch {
    return `${currency} ${Number(amount).toLocaleString('en-US')}`;
  }
};

// Evidence rendering. If ≥2 refs include a structured `txn` object, render as
// a compact transaction table; non-transaction refs (regulations, typology
// notes) render below as regular rows.
const EvidenceBlock = ({ refs }) => {
  if (!Array.isArray(refs) || refs.length === 0) return null;

  const txnRefs = refs.filter((r) => r && r.txn);
  const otherRefs = refs.filter((r) => !r || !r.txn);
  const useTable = txnRefs.length >= 2;

  return (
    <Box>
      <Typography sx={{
        color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '10px',
        fontWeight: 700, letterSpacing: '0.1em', mb: 1,
      }}>
        EVIDENCE
      </Typography>

      {useTable && (
        <Box sx={{
          mb: otherRefs.length > 0 ? 1.25 : 0,
          bgcolor: colors.canvas, borderRadius: '10px',
          overflow: 'hidden',
        }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(58px, 0.6fr) minmax(72px, 0.8fr) 28px minmax(90px, 1fr) minmax(90px, 1fr) minmax(100px, 1fr) minmax(80px, 0.9fr)',
            gap: 0, alignItems: 'center',
            px: 1.25, py: 0.9,
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.borderSubtle}`,
          }}>
            {['Entity', 'Date', '', 'Amount', 'Counterparty', 'BIC', 'Txn ID'].map((h, i) => (
              <Typography key={i} sx={{
                color: colors.textSecondary, fontFamily: typography.fontFamily,
                fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em',
              }}>
                {h}
              </Typography>
            ))}
          </Box>
          {txnRefs.map((r, i) => {
            const t = r.txn || {};
            const isOut = t.direction === 'out';
            return (
              <Box key={i} sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(58px, 0.6fr) minmax(72px, 0.8fr) 28px minmax(90px, 1fr) minmax(90px, 1fr) minmax(100px, 1fr) minmax(80px, 0.9fr)',
                gap: 0, alignItems: 'center',
                px: 1.25, py: 0.75,
                borderBottom: i === txnRefs.length - 1 ? 'none' : `1px solid ${colors.borderSubtle}`,
                '&:hover': { bgcolor: colors.surfaceElevated },
              }}>
                <Typography sx={{ fontFamily: typography.fontFamilyMono, fontSize: '11px', fontWeight: 700, color: colors.textPrimary }}>
                  {t.entity || r.label || '—'}{t.pair ? ` · P${t.pair}` : ''}
                </Typography>
                <Typography sx={{ fontFamily: typography.fontFamily, fontSize: '11px', color: colors.textSecondary }}>
                  {t.date || '—'}
                </Typography>
                <Typography sx={{
                  fontFamily: typography.fontFamilyMono, fontSize: '12px', fontWeight: 700,
                  color: isOut ? colors.critical : colors.success, textAlign: 'center',
                }}>
                  {isOut ? '→' : t.direction === 'in' ? '←' : '·'}
                </Typography>
                <Typography sx={{ fontFamily: typography.fontFamilyMono, fontSize: '11px', fontWeight: 600, color: colors.textPrimary }}>
                  {moneyFmt(t.amount, t.currency || 'USD')}
                </Typography>
                <Typography sx={{ fontFamily: typography.fontFamily, fontSize: '11px', color: colors.textSecondary }}>
                  {t.counterparty_country || '—'}
                </Typography>
                <Typography sx={{ fontFamily: typography.fontFamilyMono, fontSize: '10.5px', color: colors.textSecondary, letterSpacing: '0.02em' }}>
                  {t.bic || '—'}
                </Typography>
                <Typography sx={{ fontFamily: typography.fontFamilyMono, fontSize: '10.5px', color: colors.textSecondary }}>
                  {t.txn_id || '—'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {(useTable ? otherRefs : refs).length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
          {(useTable ? otherRefs : refs).map((e, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'baseline', gap: 1.25,
              p: 1.25, bgcolor: colors.canvas, borderRadius: '10px',
            }}>
              <Chip
                size="small"
                label={sourceLabel[e.source] || e.source}
                sx={{
                  bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer,
                  borderRadius: '4px', border: 'none',
                  fontFamily: typography.fontFamilyMono, fontSize: '9px', fontWeight: 700, height: 20,
                  letterSpacing: '0.05em',
                }}
              />
              <Typography sx={{ color: colors.textPrimary, fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: 500 }}>
                {e.label}
              </Typography>
              {e.ref && (
                <Typography sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '11px' }}>
                  — {e.ref}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const FlagDetail = ({ flag, onViewDoc, onRowHover, pdfOpen }) => (
  <Box>
    <Typography sx={{
      color: colors.textPrimary, fontFamily: typography.fontFamilyDisplay, fontWeight: 700,
      fontSize: '20px', letterSpacing: '-0.02em', lineHeight: 1.25, mb: 2,
    }}>
      {flag.title}
    </Typography>

    <DescriptionBlock text={flag.description} />

    <ExtractedDataCard extracted={flag.extracted_data} onViewDoc={onViewDoc} onRowHover={onRowHover} pdfOpen={pdfOpen} />

    <EvidenceBlock refs={flag.evidence_refs} />
  </Box>
);

const FlagDetailDialog = ({ open, onClose, cfg, flags, startIndex }) => {
  const [index, setIndex] = useState(startIndex || 0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState(null);

  useEffect(() => { if (open) setIndex(startIndex || 0); }, [open, startIndex]);
  useEffect(() => { if (!open) { setPdfDoc(null); setActivePage(null); setActiveAnchor(null); } }, [open]);
  useEffect(() => { setPdfDoc(null); setActivePage(null); setActiveAnchor(null); }, [index]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(flags.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, flags.length]);

  const handleViewDoc = useCallback((doc) => {
    setPdfDoc(doc);
    // Seed with the first row's anchor if this doc carries a `rows` map
    // (single-doc pattern). Multi-doc flags drive page/anchor on subsequent
    // row hovers instead.
    const firstRow = doc?.rows ? Object.values(doc.rows)[0] : null;
    setActivePage(firstRow?.page || 1);
    setActiveAnchor(firstRow?.text_anchor || null);
  }, []);

  const handleRowHover = useCallback((ref) => {
    if (!ref) return;
    setActivePage(ref.page);
    setActiveAnchor(ref.text_anchor || null);
    // Multi-doc flags: if the row names which document it belongs to,
    // resolve that doc from the flag's source_documents[] and switch to it.
    if (ref.doc) {
      const docs = flags[index]?.extracted_data?.source_documents;
      if (Array.isArray(docs)) {
        const match = docs.find((d) => d.key === ref.doc);
        if (match) setPdfDoc(match);
      }
    }
  }, [flags, index]);

  const handleClosePdf = useCallback(() => {
    setPdfDoc(null);
    setActivePage(null);
    setActiveAnchor(null);
  }, []);

  if (!cfg || flags.length === 0) return null;
  const flag = flags[index];
  const Icon = cfg.Icon;
  const canPrev = index > 0, canNext = index < flags.length - 1;
  const splitOpen = !!pdfDoc;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false} fullWidth={false}
      slotProps={{
        paper: {
          sx: {
            bgcolor: colors.surface, borderRadius: '16px', boxShadow: shadows.elevated,
            width: splitOpen ? '94vw' : '600px',
            maxWidth: splitOpen ? 1500 : 600,
            height: splitOpen ? '90vh' : 'auto',
            transition: 'width 0.35s ease, max-width 0.35s ease, height 0.35s ease',
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', height: splitOpen ? '100%' : 'auto', minHeight: 0 }}>
        <Box sx={{
          flex: splitOpen ? '0 0 46%' : '1 1 auto',
          display: 'flex', flexDirection: 'column',
          minWidth: 0, minHeight: 0,
          borderRight: splitOpen ? `1px solid ${colors.canvas}` : 'none',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, bgcolor: colors.canvas, flexShrink: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '10px', bgcolor: cfg.tint,
              display: 'grid', placeItems: 'center',
            }}>
              <Icon sx={{ color: cfg.accent, fontSize: 18 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: colors.textPrimary, fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '13px', letterSpacing: '-0.01em' }}>
                {cfg.label}
              </Typography>
              <Typography sx={{ color: colors.textSecondary, fontFamily: typography.fontFamilyMono, fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em' }}>
                FLAG {index + 1} OF {flags.length}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small"
              sx={{ color: colors.textSecondary, '&:hover': { color: colors.textPrimary, bgcolor: colors.surfaceElevated } }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 3, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <FlagDetail
              flag={flag}
              onViewDoc={handleViewDoc}
              onRowHover={handleRowHover}
              pdfOpen={splitOpen}
            />
          </DialogContent>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, bgcolor: colors.canvas, flexShrink: 0 }}>
            <Button
              onClick={() => setIndex((i) => Math.max(0, i - 1))} disabled={!canPrev}
              startIcon={<ChevronLeftIcon />}
              sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: 600, textTransform: 'none' }}
            >
              Previous
            </Button>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {flags.map((_, i) => (
                <Box key={i} onClick={() => setIndex(i)}
                  sx={{
                    width: i === index ? 20 : 6, height: 6, borderRadius: 999,
                    bgcolor: i === index ? cfg.accent : colors.borderSubtle,
                    cursor: 'pointer', transition: 'width 0.2s, background 0.15s',
                  }}
                />
              ))}
            </Box>
            <Button
              onClick={() => setIndex((i) => Math.min(flags.length - 1, i + 1))} disabled={!canNext}
              endIcon={<ChevronRightIcon />}
              sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily, fontSize: '12px', fontWeight: 600, textTransform: 'none' }}
            >
              Next
            </Button>
          </Box>
        </Box>

        {splitOpen && (
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
            <PdfSplitPanel
              doc={pdfDoc}
              activePage={activePage}
              activeAnchor={activeAnchor}
              onClose={handleClosePdf}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

// ── Main ──────────────────────────────────────────────────────────────

const ScoredRedFlagsList = ({ caseNumber }) => {
  const { data, isLoading } = useScoredRedFlags(caseNumber);
  const [openCategory, setOpenCategory] = useState(null);

  const flags = useMemo(() => data?.flags ?? [], [data]);

  const byCategory = useMemo(() => {
    const buckets = { fraud: [], network: [], risk_multiplier: [], sanctions: [] };
    flags.forEach((f) => {
      const k = buckets[f.category] ? f.category : 'risk_multiplier';
      buckets[k].push(f);
    });
    return buckets;
  }, [flags]);

  const roster = useMemo(() => rosterFor(flags), [flags]);

  const openCfg   = openCategory ? CATEGORY_CONFIGS[openCategory] : null;
  const openFlags = openCategory ? byCategory[openCategory] : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 0.5 }}>
        <Box>
          <Typography sx={{
            fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '16px',
            letterSpacing: '-0.01em', color: colors.textPrimary,
          }}>
            Red Flags
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: colors.textSecondary, mt: 0.35 }}>
            Grouped by signal type · click a card to expand
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: colors.accent }} />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {roster.map((catKey) => {
            const cfg = CATEGORY_CONFIGS[catKey];
            if (!cfg) return null;
            const bucket = byCategory[catKey] ?? [];
            return (
              <CategoryCard
                key={cfg.key} cfg={cfg} count={bucket.length}
                onClick={() => setOpenCategory(cfg.key)}
              />
            );
          })}
        </Box>
      )}

      <FlagDetailDialog
        open={!!openCategory} onClose={() => setOpenCategory(null)}
        cfg={openCfg} flags={openFlags} startIndex={0}
      />
    </Box>
  );
};

export default ScoredRedFlagsList;
